import React, { useRef, useState, useEffect } from "react";
import clsx from "clsx";
import { useDispatch, useSelector, useStore } from "react-redux";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import type { Transaction, TransactionFormError } from "src/interfaces";
import {
  transferTransactionSelector,
  transactionByIdSelector,
} from "src/store/transaction/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { generalLedgerAccountSelector } from "src/store/glAccounts/selector";
import { getRate } from "../mileage";
import {
  addTransactionReceipt,
  removeTransactionReceipt,
} from "src/apiService/modules/transactions";
import GoldIconButton from "src/components/GoldIconButton";
import VendorField from "src/components/VendorField";
import LoadableButton from "src/components/LoadableButton";
import Tooltip from "src/components/Tooltip";
import {
  isBankTransfer,
  TransfersAccountId,
} from "src/utils/general-ledger-accounts";
import ConfirmDialog from "src/components/ConfirmDialog";
import Uploader from "src/components/Uploader";
import { isPdfReceiptFilename } from "src/components/ReceiptDialog";
import { deleteUploadedFile } from "src/utils/storage";
import { useUploadedFile } from "src/utils/useUploadedFile";
import WalkThrough from "../EditDialogWalkThrough";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { makeStyles, type Theme } from "@material-ui/core/styles";
import Dialog from "src/components/Dialog";
import { formatDollars } from "src/utils/formatter";
import {
  getTourCurrentStep,
  getTourOpen,
  getTourStepType,
} from "src/store/tour/selector";
import { EditTransactionFormProps, initFormValues } from "./types";
import { isValidDate, isMileageAccount, signedAmount } from "./utils";
import { DefaultForm } from "./DefaultForm";
import { MileageForm } from "./MileageForm";
import { Row } from "./Row";
import BalanceTransferForm from "./BalanceTransferForm";
import {
  SplitSection,
  createEmptySplitRow,
  type SplitRowState,
} from "./SplitSection";
import { TransactionTypeField } from "./TransactionTypeField";
import { ReceiptControl } from "./ReceiptControl";
import { SingleLineBody, SplitToggleButton } from "./SingleLineBody";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
  },
  title: {
    position: "relative",
  },
  titleBalanceTransfer: {
    marginBottom: 0,
    paddingBottom: theme.spacing(1),
  },
  options: {
    position: "absolute",
    top: 0,
    right: 24,
    bottom: 0,
    display: "flex",
    alignItems: "center",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    "& > * + *": {
      marginTop: theme.spacing(1),
    },
  },
  contentBalanceTransfer: {
    paddingTop: theme.spacing(1),
  },
  container: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
}));

/**
 * Deleting the stored file is best-effort cleanup: the transaction no longer
 * points at it, and Storage may not have it anymore (a receipt deleted earlier
 * in this session, say), which must not stop the user attaching a new receipt.
 */
async function deleteReceiptFile(filename: string) {
  try {
    await deleteUploadedFile(filename);
  } catch (e) {
    console.warn("Failed to delete receipt file", filename, e);
  }
}

function TransferChoiceDialog({
  open,
  transaction,
  onClose,
  onMatch,
  onCreate,
}: {
  open: boolean;
  transaction?: Transaction;
  onClose: () => void;
  onMatch: () => void;
  onCreate: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Transfer Match</DialogTitle>
      <DialogContent>
        <DialogContentText>
          There is a transaction for {formatDollars(transaction?.amount || 0)}{" "}
          on{" "}
          {transaction?.date
            ? transaction.date.toLocaleString(undefined, {
                day: "numeric",
                month: "numeric",
                year: "numeric",
              })
            : ""}{" "}
          with a charge description of <i>"{transaction?.name || ""}"</i>. If
          this is the other side of the transfer, confirm the match below. If
          not, select "Create New Transfer".
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCreate} color="primary" variant="contained">
          Create New Transfer
        </Button>
        <Button onClick={onMatch} color="primary" variant="contained">
          Match
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function EditTransactionForm({
  data,
  loading,
  onSave,
  onDelete,
  noCopyExpenseButton,
  onMaxWidthChange,
}: EditTransactionFormProps) {
  const recordEvent = useRecordEvent();
  const base = data?.transaction;
  const dateRef = useRef<HTMLDivElement>(null);
  const [dialog, setDialog] = useState<
    | undefined
    | { type: "walkthrough" }
    | {
        type: "transfer-match";
        values: Transaction;
        transaction: Transaction;
      }
    | {
        type: "confirm-non-inventory";
        values: Partial<Transaction>;
      }
    | {
        type: "receipt";
      }
  >(undefined);
  const classes = useStyles();
  const store = useStore();
  const dispatch = useDispatch();
  const [accountOpen, setAccountOpen] = useState(false);
  const [values, setValues] = useState<Partial<Transaction>>(
    base || initFormValues
  );
  const receipt = useUploadedFile(values.receipt_filename, {
    label: "receipt",
  });
  const isUpdate = !!values.id;
  const [isBalanceTransferMode, setIsBalanceTransferMode] = useState(
    !!(base?.gl_account_id === "transfers")
  );
  useEffect(() => {
    if (base?.gl_account_id === "transfers") setIsBalanceTransferMode(true);
  }, [base?.gl_account_id]);
  const [errors, setErrors] = useState<TransactionFormError>({});
  const [isSplitMode, setIsSplitMode] = useState<boolean>(
    data?.mode === "split"
  );
  const openedAsMoneyIn =
    Object.is(base?.amount, -0) ||
    (typeof base?.amount === "number"
      ? base.amount < 0
      : (data?.split?.total ?? 0) < 0);
  const [splitTotal, setSplitTotal] = useState<number | "">(() => {
    // Show the amount as it was entered. Money In is tracked on values.amount
    // and reverses the sign only when the split is saved.
    if (data?.split) return signedAmount(data.split.total, openedAsMoneyIn);
    return typeof base?.amount === "number"
      ? signedAmount(base.amount, openedAsMoneyIn)
      : "";
  });
  const [splitRows, setSplitRows] = useState<SplitRowState[]>(() => {
    if (data?.split?.rows?.length) {
      return data.split.rows.map((r) => ({
        key: r.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id: r.id,
        amount:
          typeof r.amount === "number"
            ? signedAmount(r.amount, openedAsMoneyIn)
            : r.amount,
        gl_account_id: r.gl_account_id,
        account: r.account,
        description: r.description,
      }));
    }
    return [createEmptySplitRow(), createEmptySplitRow()];
  });
  const isMileage = isMileageAccount(values.gl_account_id, values.account);
  const isMoneyIn = Object.is(values.amount, -0) || (values.amount || 0) < 0;
  const account = useSelector((state) =>
    generalLedgerAccountSelector(state, {
      id: values.gl_account_id,
      name: values.account,
    })
  );
  const mappedTransaction = useSelector((state) =>
    transactionByIdSelector(state, values.mapped)
  );
  const mappedTransactionAccount = mappedTransaction?.integration_name;
  // Split transactions can't be linked to inventory items, so suppress the
  // inventory linking UI even when a split line uses an inventory GL account.
  const isInventory = !isSplitMode && account?.type?.[1] === "inventory";

  // A split can't carry sale/inventory/journal/transfer links, so block the
  // toggle for those transactions rather than silently dropping the links.
  const hasBlockingLink = !!(
    values.orderId ||
    (values.items && values.items.length > 0) ||
    values.journal_entry_id ||
    values.mapped
  );
  const isSplitToggleDisabled = isBalanceTransferMode || hasBlockingLink;
  const splitToggleTitle = hasBlockingLink
    ? "Transactions linked to a sale can't be split. Clear the linked sale at the bottom of the form to split the transaction."
    : "Split Transaction";

  const onWidthChangeRef = useRef(onMaxWidthChange);
  onWidthChangeRef.current = onMaxWidthChange;

  useEffect(() => {
    onWidthChangeRef.current(
      isBalanceTransferMode ? false : isInventory ? "md" : "sm"
    );
  }, [isInventory, isBalanceTransferMode]);

  const handleDelete = () => {
    onDelete();
  };

  const splitSum = splitRows.reduce(
    (acc, r) => acc + (typeof r.amount === "number" ? r.amount : 0),
    0
  );
  const splitTotalNum = typeof splitTotal === "number" ? splitTotal : 0;
  const splitBalanced = Math.abs(splitTotalNum - splitSum) < 0.005;
  const splitRowsValid =
    splitRows.length > 0 &&
    splitRows.every(
      (r) =>
        r.gl_account_id &&
        r.account &&
        typeof r.amount === "number" &&
        r.amount !== 0
    );
  const splitValid =
    isSplitMode &&
    typeof splitTotal === "number" &&
    splitTotal !== 0 &&
    splitRowsValid &&
    splitBalanced;

  const validateForm = (
    values: Partial<Transaction>
  ): values is Transaction => {
    let hasError = false;
    let newErrors: TransactionFormError = { ...errors };

    if (isSplitMode) {
      // In split mode, the GL account on `values` is not set; validation of
      // per-row GL and matching totals is tracked separately via splitValid.
      newErrors.account = undefined;
      if (!splitValid) {
        hasError = true;
        newErrors.amount =
          splitTotal === "" || splitTotal === 0
            ? "Total amount is required"
            : !splitBalanced
            ? "Split amounts must equal the total"
            : "All split rows require an amount and GL account";
      } else {
        newErrors.amount = undefined;
      }
    } else {
      if (values.account === undefined || values.account.trim() === "") {
        hasError = true;
        newErrors.account = "Required field!";
      } else {
        newErrors.account = undefined;
      }

      if (!values.amount) {
        hasError = true;
        newErrors.amount = "Required field!";
      } else {
        newErrors.amount = undefined;
      }
    }

    if (!isValidDate(values.date)) {
      hasError = true;
      newErrors.date = "Invalid date format";
    } else {
      if (!isSplitMode && isMileage && !getRate(values.date)) {
        hasError = true;
        newErrors.date =
          "We don't have the rate for the date you specified. Check back in later or send us a message to support@myresellergenie.com";
      } else {
        newErrors.date = undefined;
      }
    }

    const checkMileage =
      !isSplitMode && isMileageAccount(values.gl_account_id, values.account);
    if (!checkMileage && !values.integration_name) {
      hasError = true;
      newErrors.integration_name = "Required field!";
    } else {
      newErrors.integration_name = undefined;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const handleSave = (event: any, next?: boolean) => {
    if (event?.preventDefault) event.preventDefault();

    if (validateForm(values)) {
      if (isSplitMode) {
        const rows = splitRows.map((r) => ({
          ...(r.id ? { id: r.id } : {}),
          amount: signedAmount(
            typeof r.amount === "number" ? r.amount : 0,
            isMoneyIn
          ),
          gl_account_id: r.gl_account_id!,
          account: r.account!,
          description: r.description ?? "",
        }));
        const splitPayload = {
          ...values,
          amount: signedAmount(splitTotalNum, isMoneyIn),
        } as Transaction;
        onSave(
          splitPayload,
          {
            next: !!next,
            split: {
              split_group_id: data?.split?.split_group_id,
              rows,
            },
          },
          receipt.file
        );
        return;
      }

      const mapped = isBankTransfer(account?.type);
      if (mapped && !values.mapped) {
        const transferTransaction = transferTransactionSelector(
          store.getState(),
          values
        );

        if (transferTransaction) {
          setDialog({
            type: "transfer-match",
            transaction: transferTransaction,
            values,
          });
          return;
        }
      }

      onSave(
        values,
        {
          next: !!next,
          mapped:
            mapped && !values.mapped
              ? {
                  date: values.date!,
                  bank_account_id: values.bank_account_id!,
                  integration_name: values.integration_name ?? "",
                  description: values.description ?? "",
                }
              : undefined,
        },
        receipt.file
      );
    } else {
      toast.error("Some required information is missing or incomplete");
    }
  };

  const handleCopy = () => {
    setValues(({ items, mapped, ...v }) => ({
      ...v,
      id: "",
    }));
  };

  const handleChange =
    (prop: keyof Transaction) => (event: { target: { value?: any } }) => {
      const value = event.target.value;
      setValues((values) => ({ ...values, [prop]: value }));
    };

  const handleToggleSplit = () => {
    if (isSplitToggleDisabled) return;
    if (isSplitMode) {
      // Unsplit: collapse back to a single-row transaction. Keep the total,
      // with Money In applied, so the single form still shows the right direction.
      setIsSplitMode(false);
      setValues((v) => ({
        ...v,
        amount:
          typeof splitTotal === "number"
            ? signedAmount(splitTotal, isMoneyIn)
            : v.amount,
      }));
    } else {
      setIsSplitMode(true);
      setSplitTotal((prev) =>
        prev === "" && typeof values.amount === "number"
          ? Math.abs(values.amount)
          : prev
      );
      setSplitRows((prev) => {
        let rows = prev.length >= 2 ? prev : [...prev, createEmptySplitRow()];
        // Descriptions now live per-line, so seed the first row with the
        // existing top-level description when it isn't already set.
        if (values.description && !rows[0].description) {
          rows = [
            { ...rows[0], description: values.description },
            ...rows.slice(1),
          ];
        }
        return rows;
      });
      // A split transaction can't be linked to a sale, so clear any linked
      // sale when switching into split mode.
      setValues((v) => ({ ...v, orderId: undefined }));
    }
  };

  const receiptIsPdf = receipt.file
    ? isPdfReceiptFilename(receipt.file.name)
    : !!values?.receipt_filename &&
      isPdfReceiptFilename(values.receipt_filename);

  const currentStep = useSelector(getTourCurrentStep);
  const isOpen = useSelector(getTourOpen);
  const tourStep = useSelector(getTourStepType);

  useEffect(() => {
    if (tourStep === "chart_of_accounts" && isOpen) {
      setAccountOpen(true);
    }
  }, [tourStep, currentStep, isOpen]);

  const Form = isMileage ? MileageForm : DefaultForm;

  return (
    <>
      <WalkThrough
        open={dialog?.type === "walkthrough"}
        onClose={() => setDialog(undefined)}
        isUpdate={isUpdate}
      />
      <TransferChoiceDialog
        open={dialog?.type === "transfer-match"}
        transaction={
          dialog?.type === "transfer-match" ? dialog.transaction : undefined
        }
        onClose={() => setDialog(undefined)}
        onMatch={() => {
          if (
            dialog?.type === "transfer-match" &&
            dialog.values &&
            dialog.transaction
          ) {
            onSave(
              dialog.values,
              {
                mapped: {
                  id: dialog.transaction.id,
                  date: dialog.values.date!,
                  description: dialog.values.description ?? "",
                },
              },
              receipt.file
            );
          }
          setDialog(undefined);
        }}
        onCreate={() => {
          if (dialog?.type === "transfer-match" && dialog.values) {
            onSave(
              dialog.values,
              {
                mapped: {
                  date: dialog.values.date!,
                  bank_account_id: dialog.values.bank_account_id!,
                  integration_name: dialog.values.integration_name ?? "",
                  description: dialog.values.description ?? "",
                },
              },
              receipt.file
            );
          }
          setDialog(undefined);
        }}
      />
      <ConfirmDialog
        open={dialog?.type === "confirm-non-inventory"}
        title="Are you sure?"
        text="Are you sure you want to change this to a non-inventory general ledger account? This will unlink all the items that were attached to this transaction."
        onCancel={() => setDialog(undefined)}
        onConfirm={() => {
          if (dialog?.type !== "confirm-non-inventory") return;
          setValues(({ items, ...v }) => ({ ...v, ...dialog.values }));
          setDialog(undefined);
        }}
      />

      <form
        noValidate
        autoComplete="off"
        onSubmit={handleSave}
        className={classes.root}
      >
        <DialogTitle
          className={clsx(
            classes.title,
            isBalanceTransferMode && classes.titleBalanceTransfer
          )}
        >
          {isBalanceTransferMode
            ? isUpdate
              ? "Edit Balance Transfer"
              : "Balance Transfer"
            : `${isUpdate ? "Edit" : "Add"} Transaction`}
          <span className={classes.options}>
            {isMileage && (
              <Tooltip title="The mileage entry form uses current US standard mileage rates provided by the IRS to calculate mileage expense.">
                <InfoOutlinedIcon />
              </Tooltip>
            )}
            {isUpdate && !isBalanceTransferMode && (
              <IconButton
                onClick={handleDelete}
                disabled={loading}
                className={classes.deleteButton}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            )}
          </span>
        </DialogTitle>
        <DialogContent
          className={clsx(
            classes.content,
            isBalanceTransferMode && classes.contentBalanceTransfer
          )}
        >
          {isBalanceTransferMode ? (
            <BalanceTransferForm
              data={
                isUpdate
                  ? {
                      ...values,
                      gl_account_id: "transfers",
                      account: "Transfers",
                    }
                  : undefined
              }
              loading={loading}
              onSave={onSave}
              onSwitchToDefaultForm={() => {
                setIsBalanceTransferMode(false);
                setValues((prev) => ({
                  ...prev,
                  gl_account_id: undefined,
                  account: undefined,
                }));
              }}
              setDialog={setDialog}
            />
          ) : isSplitMode ? (
            <div className={classes.container}>
              <TransactionTypeField
                isMoneyIn={isMoneyIn}
                loading={loading}
                isUpdate={isUpdate}
                onDirectionChange={(nextMoneyIn) => {
                  // The typed amounts stay put. Money Out saves that sign. Money In
                  // saves the opposite sign.
                  setValues((v) => ({
                    ...v,
                    amount: signedAmount(Math.abs(v?.amount || 0), nextMoneyIn),
                  }));
                }}
                onJournalEntry={() => dispatch(setActiveDialog("add_journal"))}
              />
              <Row
                text="The category of the transaction."
                InfoTooltipProps={{
                  id: "transaction-edit-dialog-general-ledger-account-tooltip",
                }}
                id={"general-ledger-row"}
              >
                <Box flex={1} display="flex" alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    Transaction split across multiple accounts
                  </Typography>
                </Box>
                <SplitToggleButton
                  disabled={loading || isSplitToggleDisabled}
                  active
                  onClick={handleToggleSplit}
                  title={splitToggleTitle}
                />
              </Row>
              <Row text="Where the purchase was made.">
                <VendorField
                  margin="dense"
                  disabled={loading}
                  value={values.vendor}
                  onChange={(vendor) => {
                    setValues((v) => ({ ...v, vendor }));
                  }}
                />
              </Row>
              <Form
                values={values}
                errors={errors}
                handleChange={handleChange}
                loading={loading}
                setValues={setValues}
                dateRef={dateRef}
                hideAmount
                hideDescription
              />
              <SplitSection
                rows={splitRows}
                onChangeRows={setSplitRows}
                total={splitTotal}
                onChangeTotal={setSplitTotal}
                disabled={loading}
                onCollapseToSingle={(remaining) => {
                  setIsSplitMode(false);
                  setValues((v) => ({
                    ...v,
                    gl_account_id: remaining.gl_account_id,
                    account: remaining.account,
                    description: remaining.description ?? v.description,
                    amount:
                      typeof splitTotal === "number"
                        ? signedAmount(splitTotal, isMoneyIn)
                        : v.amount,
                  }));
                }}
              />
            </div>
          ) : (
            <SingleLineBody
              values={values}
              setValues={setValues}
              errors={errors}
              loading={loading}
              handleChange={handleChange}
              dateRef={dateRef}
              isMoneyIn={isMoneyIn}
              isMileage={isMileage}
              isInventory={isInventory}
              account={account}
              mappedTransactionAccount={mappedTransactionAccount}
              accountName={base?.account}
              accountOpen={accountOpen}
              setAccountOpen={setAccountOpen}
              tourOpen={isOpen}
              onEnterBalanceTransfer={() => {
                setValues((v) => ({
                  ...v,
                  gl_account_id: TransfersAccountId,
                  account: "Transfers",
                }));
                setIsBalanceTransferMode(true);
              }}
              onRequestNonInventoryConfirm={(confirmValues) =>
                setDialog({
                  type: "confirm-non-inventory",
                  values: confirmValues,
                })
              }
              splitToggleDisabled={isSplitToggleDisabled}
              splitToggleTitle={splitToggleTitle}
              onToggleSplit={handleToggleSplit}
            />
          )}
        </DialogContent>
        {!isBalanceTransferMode && (
          <DialogActions>
            <Box mt={-1.5}>
              <Tooltip title="Need help? Click here.">
                <GoldIconButton
                  onClick={() => setDialog({ type: "walkthrough" })}
                />
              </Tooltip>
            </Box>
            <Box flex={1} />
            <ReceiptControl
              receiptUrl={receipt.url}
              hasReceipt={receipt.hasFile}
              receiptLoading={receipt.loading}
              receiptIsPdf={receiptIsPdf}
              loading={loading}
              open={dialog?.type === "receipt"}
              onOpen={() => setDialog({ type: "receipt" })}
              onClose={() => setDialog(undefined)}
              onConfirm={async (newReceiptFile?: File) => {
                if (newReceiptFile) {
                  recordEvent({
                    type: "feature-adoption",
                    feature: "receipt-upload",
                  });
                }
                if (isUpdate && newReceiptFile) {
                  const previousFilename = values?.receipt_filename;
                  const filename = await addTransactionReceipt(
                    base?.id,
                    newReceiptFile
                  );
                  if (previousFilename && previousFilename !== filename) {
                    await deleteReceiptFile(previousFilename);
                  }
                  setValues((v) => ({ ...v, receipt_filename: filename }));
                }
                receipt.setFile(newReceiptFile);
                setDialog(undefined);
                if (newReceiptFile) toast.success("Receipt attached.");
              }}
              onDelete={async () => {
                const storedFilename = values?.receipt_filename;
                if (storedFilename) {
                  await removeTransactionReceipt(values.id);
                  setValues(({ receipt_filename, ...v }) => v);
                  await deleteReceiptFile(storedFilename);
                }
                receipt.clear();
                toast.success("Receipt deleted.");
              }}
            />
            {isUpdate ? (
              noCopyExpenseButton ? null : (
                <Button
                  onClick={handleCopy}
                  color="primary"
                  variant="contained"
                  disabled={loading}
                  id="transaction-edit-dialog-copy-expense-button"
                  tabIndex={-1}
                >
                  Duplicate
                </Button>
              )
            ) : (
              <>
                <Uploader
                  id="transaction-edit-dialog-bulk-upload-button"
                  importerType="expenses"
                  label="Bulk Upload"
                  tabIndex={-1}
                />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || (isSplitMode && !splitValid)}
                  onClick={() => handleSave({}, true)}
                >
                  Add and Next
                </Button>
              </>
            )}
            <LoadableButton
              color="primary"
              variant="contained"
              loading={loading}
              type="submit"
              disabled={isSplitMode && !splitValid}
            >
              {isUpdate ? "Update" : "Add"}
            </LoadableButton>
          </DialogActions>
        )}
      </form>
    </>
  );
}
